import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, Phone, Mail, Clock, MapPin } from "lucide-react";
import { BUSINESS, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in your name, email, phone and service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request sent! We'll be in touch within 24 hours.", {
        description: `Thanks ${form.name}, we've received your request.`,
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        city: "",
        service: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data)
          ? "Please check your inputs"
          : "Something went wrong. Please try again or call us.");
      toast.error("Could not send request", { description: String(detail) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-32 bg-[#050505] overflow-hidden"
      data-testid="contact-section"
    >
      <div className="absolute -top-40 right-0 w-[500px] h-[500px] rounded-full bg-[#C4FF00]/5 blur-[120px] pointer-events-none" />
      <div className="mx-auto max-w-7xl px-6 md:px-10 relative">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Left info */}
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#C4FF00] mb-4">
              — Get a Quote
            </div>
            <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter text-white leading-[0.95]">
              Let&apos;s make it
              <br />
              <span className="text-[#00BFFF]">Spotless.</span>
            </h2>
            <p className="mt-6 text-lg text-zinc-400 max-w-md">
              Tell us what needs washing and we&apos;ll send back a fair, honest quote —
              typically the same day.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={BUSINESS.phoneHref}
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="h-12 w-12 rounded-xl border border-zinc-800 bg-black grid place-items-center text-[#C4FF00] group-hover:border-[#C4FF00] transition-colors">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">
                    Call or Text
                  </div>
                  <div className="text-lg font-bold text-white group-hover:text-[#C4FF00] transition-colors">
                    {BUSINESS.phone}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${BUSINESS.email}`}
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="h-12 w-12 rounded-xl border border-zinc-800 bg-black grid place-items-center text-[#00BFFF] group-hover:border-[#00BFFF] transition-colors">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">
                    Email
                  </div>
                  <div className="text-lg font-bold text-white group-hover:text-[#00BFFF] transition-colors">
                    {BUSINESS.email}
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl border border-zinc-800 bg-black grid place-items-center text-white">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">
                    Hours
                  </div>
                  <div className="text-base font-bold text-white">
                    {BUSINESS.hours}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl border border-zinc-800 bg-black grid place-items-center text-white">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">
                    Service Area
                  </div>
                  <div className="text-base font-bold text-white">
                    West Palm Beach → Miami
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <form
            onSubmit={submit}
            className="lg:col-span-3 rounded-2xl border border-zinc-800 bg-black p-6 md:p-10"
            data-testid="quote-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <FieldWrap label="Your Name" htmlFor="q-name">
                <Input
                  id="q-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Doe"
                  className="bg-[#0A0A0A] border-zinc-800 text-white h-12 focus-visible:border-[#C4FF00] focus-visible:ring-0"
                  data-testid="quote-input-name"
                  required
                />
              </FieldWrap>
              <FieldWrap label="Phone" htmlFor="q-phone">
                <Input
                  id="q-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(555) 123-4567"
                  className="bg-[#0A0A0A] border-zinc-800 text-white h-12 focus-visible:border-[#C4FF00] focus-visible:ring-0"
                  data-testid="quote-input-phone"
                  required
                />
              </FieldWrap>
              <FieldWrap label="Email" htmlFor="q-email">
                <Input
                  id="q-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  className="bg-[#0A0A0A] border-zinc-800 text-white h-12 focus-visible:border-[#C4FF00] focus-visible:ring-0"
                  data-testid="quote-input-email"
                  required
                />
              </FieldWrap>
              <FieldWrap label="City" htmlFor="q-city">
                <Input
                  id="q-city"
                  value={form.city}
                  onChange={update("city")}
                  placeholder="Boca Raton"
                  className="bg-[#0A0A0A] border-zinc-800 text-white h-12 focus-visible:border-[#C4FF00] focus-visible:ring-0"
                  data-testid="quote-input-city"
                />
              </FieldWrap>
              <div className="sm:col-span-2">
                <FieldWrap label="Service Needed" htmlFor="q-service">
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm((p) => ({ ...p, service: v }))}
                  >
                    <SelectTrigger
                      id="q-service"
                      className="bg-[#0A0A0A] border-zinc-800 text-white h-12 focus:ring-0 focus:border-[#C4FF00] data-[placeholder]:text-zinc-500"
                      data-testid="quote-input-service"
                    >
                      <SelectValue placeholder="Pick a service..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-zinc-800 text-white">
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s.id}
                          value={s.title}
                          className="focus:bg-[#C4FF00] focus:text-black"
                        >
                          {s.title}
                        </SelectItem>
                      ))}
                      <SelectItem
                        value="Multiple Services"
                        className="focus:bg-[#C4FF00] focus:text-black"
                      >
                        Multiple Services / Not Sure
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FieldWrap>
              </div>
              <div className="sm:col-span-2">
                <FieldWrap label="Tell us about the project" htmlFor="q-message">
                  <Textarea
                    id="q-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="1-story home, ~2,400 sqft, driveway is stained near the garage..."
                    rows={5}
                    className="bg-[#0A0A0A] border-zinc-800 text-white focus-visible:border-[#C4FF00] focus-visible:ring-0 resize-none"
                    data-testid="quote-input-message"
                  />
                </FieldWrap>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="mt-8 w-full sm:w-auto bg-[#C4FF00] text-black hover:bg-[#D4FF33] font-bold uppercase tracking-widest h-14 px-8 rounded-full disabled:opacity-60"
              data-testid="quote-submit"
            >
              <Send className="h-4 w-4 mr-2" />
              {loading ? "Sending..." : "Send Free Quote Request"}
            </Button>
            <p className="mt-4 text-xs text-zinc-500">
              We&apos;ll never share your info. Response within 24 hours.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function FieldWrap({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={htmlFor}
        className="text-xs uppercase tracking-widest font-bold text-zinc-400"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}
